import { Component, ChangeDetectorRef } from '@angular/core';
import { FormGroup, Validators, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { LoginService } from '@core/services/login/login.service';
import { UtilityService } from '@core/services/util/utility.service';
import { StorageService } from '@core/services/util/storage.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { SentryBreadcrumbsService } from '@app/core/services/sentry-breadcrumbs.service';
import { PonchoUIModule } from '@poncho/ui';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import RetryOnError from '@app/core/decorators/retry-on-406';

@Component({
    selector: 'app-main-mobile-number',
    templateUrl: './main-mobile-number.component.html',
    styleUrls: ['./main-mobile-number.component.scss'],
    standalone: true,
    imports: [MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, PonchoUIModule]
})
export class MainMobileNumberComponent {

	mobileNumberForm: FormGroup;
    continueInProgress = false;

    constructor(
        private loginService: LoginService,
        private utilityService: UtilityService,
        private authenticationService: AuthenticationService,
        private storageService: StorageService,
        private toastrService: ToastrService,
        private cdr: ChangeDetectorRef,
        private breadCrumb: SentryBreadcrumbsService
    ) {
        const phoneRegex = this.utilityService.phoneRegex;
        this.mobileNumberForm = new FormGroup({
            mobile: new FormControl('', Validators.compose([Validators.pattern(phoneRegex)]))
        });
    }

    async checkPhoneStatus() {
        if (!this.mobileNumberForm.valid) {
            this.utilityService.markFormFieldsAsTouched(this.mobileNumberForm);
            return;
        }

        this.continueInProgress = true;

        try {
            await this.loginService.ensureSessionId();
            const phoneNumber = this.mobileNumberForm.value.mobile;
            const response = await this.phoneStatusWithRetry(phoneNumber);
            if ((response as any)?.meta) {
                this.storageService.set('phone_number', phoneNumber);
                this.handlePhoneStatusResponse(response);
            }
			this.breadCrumb.add('auth', 'MainMobileNumberComponent Success', null);
        } catch (error: any) {
            this.toastrService.openSnackBar(error?.meta?.message, 'OKAY');
            this.breadCrumb.add('auth', 'MainMobileNumberComponent Error', null);
        } finally {
            this.continueInProgress = false;
            this.cdr.markForCheck();
        }
    }

	@RetryOnError(2)
    private async phoneStatusWithRetry(phoneNumber: string, retryCount?: number): Promise<any> {
		const sendAppCheckToken = retryCount === undefined || retryCount < 2;
		return await this.loginService.phoneStatus(phoneNumber, sendAppCheckToken);
    }

    private handlePhoneStatusResponse(data: any): void {
        switch (data.meta.code) {
            case 200:
                this.authenticationService.nextStep('signin', 'login-with-password');
                break;
            case 201:
                this.toastrService.openSnackBar(data.meta.message, 'OKAY');
                this.authenticationService.nextStep('otp-verification', 'otp-verification');
                break;
            case 202:
                this.authenticationService.nextStep('signup-details', 'signup-details');
                break;
            default:
                this.toastrService.openSnackBar(data.meta.message, 'OKAY');
                break;
        }
    }

    allowOnlyNumbers(event: KeyboardEvent | ClipboardEvent): boolean {
        return this.utilityService.allowOnlyNumbers(event);
    }
}
